import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  DataTable,
  Field,
  FormRow,
  Input,
  RightDrawer,
} from '@projexn/ui';
import { api } from '../lib/api';

interface Takeoff {
  id: string;
  tenderId: string;
  name: string;
  pdfAttachmentId: string | null;
  pageCount: number;
  measurements: Measurement[];
  createdAt: string;
}

interface Measurement {
  id: string;
  page: number;
  measurementType: 'length' | 'area' | 'count' | 'volume';
  value: number;
  unit: string;
  label: string | null;
  geometry: Array<{ x: number; y: number }>;
}

interface Tender {
  id: string;
  name: string;
  metadata?: Record<string, unknown>;
}

function refOf(t: Tender): string {
  const r = t.metadata?.reference;
  return typeof r === 'string' ? r : t.id.slice(-6).toUpperCase();
}

export function TakeoffsPage() {
  const qc = useQueryClient();
  const [tenderId, setTenderId] = useState<string>('');
  const [selected, setSelected] = useState<Takeoff | null>(null);
  const [creating, setCreating] = useState(false);

  const tendersQ = useQuery({
    queryKey: ['tenders'],
    queryFn: () => api.get<{ items: Tender[] }>('/api/tenders'),
  });
  const takeoffsQ = useQuery({
    queryKey: ['takeoffs', { tenderId }],
    queryFn: () =>
      tenderId
        ? api.get<Takeoff[]>(`/api/tenders/${tenderId}/takeoffs`)
        : Promise.resolve([]),
    enabled: !!tenderId,
  });

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Takeoffs</h1>
          <p className="text-sm text-zinc-500">Measurements extracted from tender drawings.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={tenderId}
            onChange={(e) => setTenderId(e.target.value)}
            className="h-9 rounded-md border border-zinc-300 bg-white px-3 text-sm"
          >
            <option value="">Choose a tender…</option>
            {(tendersQ.data?.items ?? []).map((t) => (
              <option key={t.id} value={t.id}>{refOf(t)} · {t.name}</option>
            ))}
          </select>
          <Button onClick={() => setCreating(true)} disabled={!tenderId}>
            New takeoff
          </Button>
        </div>
      </header>

      {!tenderId ? (
        <Card className="p-12 text-center text-sm text-zinc-500">
          Select a tender to view its takeoffs.
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <DataTable
            rows={takeoffsQ.data ?? []}
            rowKey={(r) => r.id}
            onRowClick={(r) => setSelected(r)}
            columns={[
              { key: 'name', header: 'Name', render: (r) => r.name },
              { key: 'pages', header: 'Pages', render: (r) => r.pageCount.toString() },
              { key: 'measurements', header: 'Measurements', render: (r) => r.measurements.length.toString() },
              {
                key: 'totals',
                header: 'Totals',
                render: (r) => totalsSummary(r.measurements),
              },
            ]}
            empty={<p className="px-6 py-12 text-center text-sm text-zinc-500">No takeoffs for this tender.</p>}
          />
        </Card>
      )}

      <RightDrawer open={creating} onClose={() => setCreating(false)} title="New takeoff" width="lg">
        <NewTakeoffForm
          tenderId={tenderId}
          onCreated={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['takeoffs'] });
          }}
        />
      </RightDrawer>

      <RightDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.name}
        width="xl"
      >
        {selected && <TakeoffPanel takeoff={selected} />}
      </RightDrawer>
    </div>
  );
}

function NewTakeoffForm({
  tenderId,
  onCreated,
}: {
  tenderId: string;
  onCreated: () => void;
}) {
  const [name, setName] = useState('');
  const [pageCount, setPageCount] = useState<number>(1);

  const mutate = useMutation({
    mutationFn: () =>
      api.post('/api/takeoffs', {
        tenderId,
        name,
        pageCount,
      }),
    onSuccess: onCreated,
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); mutate.mutate(); }} className="space-y-4">
      <FormRow>
        <Field label="Name" required>
          <Input required value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
      </FormRow>
      <FormRow>
        <Field label="Page count">
          <Input
            type="number"
            min={1}
            value={pageCount}
            onChange={(e) => setPageCount(Math.max(1, Number(e.target.value)))}
          />
        </Field>
      </FormRow>
      {mutate.isError && (
        <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
          {(mutate.error as Error).message}
        </p>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" loading={mutate.isPending}>Create</Button>
      </div>
    </form>
  );
}

function totalsSummary(ms: Measurement[]): string {
  const sums: Partial<Record<Measurement['measurementType'], number>> = {};
  for (const m of ms) {
    sums[m.measurementType] = (sums[m.measurementType] ?? 0) + m.value;
  }
  const parts: string[] = [];
  if (sums.length) parts.push(`L:${sums.length.toFixed(1)}`);
  if (sums.area) parts.push(`A:${sums.area.toFixed(1)}`);
  if (sums.count) parts.push(`#:${sums.count}`);
  return parts.join('  ') || '—';
}

function TakeoffPanel({ takeoff }: { takeoff: Takeoff }) {
  const qc = useQueryClient();
  const takeoffQ = useQuery({
    queryKey: ['takeoffs', takeoff.id],
    queryFn: () => api.get<Takeoff>(`/api/takeoffs/${takeoff.id}`),
    initialData: takeoff,
  });

  const [form, setForm] = useState({
    label: '',
    measurementType: 'length' as Measurement['measurementType'],
    value: 0,
    unit: 'm',
    page: 1,
  });

  const add = useMutation({
    mutationFn: () =>
      api.post(`/api/takeoffs/${takeoff.id}/measurements`, {
        page: form.page,
        measurementType: form.measurementType,
        value: form.value,
        unit: form.unit,
        label: form.label || undefined,
        geometry: [],
      }),
    onSuccess: () => {
      setForm({ ...form, label: '', value: 0 });
      qc.invalidateQueries({ queryKey: ['takeoffs', takeoff.id] });
      qc.invalidateQueries({ queryKey: ['takeoffs'] });
    },
  });

  const measurements = takeoffQ.data?.measurements ?? [];

  return (
    <div className="space-y-4">
      <section className="rounded-md border border-zinc-200 bg-zinc-50 p-4 text-sm">
        <p className="text-zinc-600">
          PDF rendering (PDF.js) lands in a follow-up. For now, log measurements directly —
          they aggregate into the takeoff totals shown in the table.
        </p>
      </section>

      <Card className="overflow-hidden">
        <DataTable
          rows={measurements}
          rowKey={(r) => r.id}
          columns={[
            { key: 'label', header: 'Label', render: (r) => r.label ?? '—' },
            { key: 'type', header: 'Type', render: (r) => r.measurementType },
            { key: 'value', header: 'Value', render: (r) => `${r.value} ${r.unit}` },
            { key: 'page', header: 'Page', render: (r) => r.page.toString() },
          ]}
          empty={<p className="px-6 py-12 text-center text-sm text-zinc-500">No measurements logged.</p>}
        />
      </Card>

      <form
        onSubmit={(e) => { e.preventDefault(); add.mutate(); }}
        className="rounded-md border border-zinc-200 p-3"
      >
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">Add measurement</p>
        <FormRow>
          <Field label="Label">
            <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
          </Field>
          <Field label="Type">
            <select
              value={form.measurementType}
              onChange={(e) => setForm({ ...form, measurementType: e.target.value as Measurement['measurementType'] })}
              className="h-9 w-full rounded-md border border-zinc-300 bg-white px-3 text-sm"
            >
              <option value="length">Length</option>
              <option value="area">Area</option>
              <option value="count">Count</option>
              <option value="volume">Volume</option>
            </select>
          </Field>
        </FormRow>
        <FormRow>
          <Field label="Value" required>
            <Input
              type="number"
              required
              min={0}
              step="0.01"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: Number(e.target.value) })}
            />
          </Field>
          <Field label="Unit">
            <Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
          </Field>
          <Field label="Page">
            <Input
              type="number"
              min={1}
              value={form.page}
              onChange={(e) => setForm({ ...form, page: Math.max(1, Number(e.target.value)) })}
            />
          </Field>
        </FormRow>
        <div className="flex justify-end pt-2">
          <Button type="submit" size="sm" loading={add.isPending}>Add</Button>
        </div>
      </form>
    </div>
  );
}
